/**
 * Domain-layer diagnostic tracing.
 *
 * Default behavior: SILENT in both dev and prod — explicit opt-in
 * required. Earlier in the migration the dev default was "enabled,"
 * which surfaced useful traces but also flooded the console for any
 * developer not actively debugging the domain layer.
 *
 * Opt in at runtime via the browser console:
 *   - Enable:   localStorage.setItem('domainTrace', '1'); location.reload();
 *   - Disable:  localStorage.removeItem('domainTrace'); location.reload();
 *
 * NOTE: snapshots traced through these calls may include PHI fields
 * (e.g., dicomPatientName) if the caller passes a snapshot. Same
 * privacy posture as LoggingPersistenceService — production must
 * remain default-disabled.
 */

const ENABLE_KEY = 'domainTrace';

function isEnabled(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        return window.localStorage.getItem(ENABLE_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * Format vec-like values (Float32Array, gl-matrix vec2/vec3/vec4, plain
 * number arrays) as inline `(x, y, z)` strings so they appear directly
 * in console output instead of as collapsed `Array(3)` references.
 * Recursively walks plain objects so nested vecs in trace payloads
 * are also formatted.
 */
function formatVecsInline(value: unknown): unknown {
    if (
        value instanceof Float32Array ||
        value instanceof Float64Array ||
        (Array.isArray(value) && value.every((v) => typeof v === 'number'))
    ) {
        const arr = Array.from(value as ArrayLike<number>);
        if (arr.length > 0 && arr.length <= 16) {
            return `(${arr.map((n) => n.toFixed(3)).join(', ')})`;
        }
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            out[k] = formatVecsInline(v);
        }
        return out;
    }
    return value;
}

function log(namespace: string, ...args: unknown[]): void {
    if (!isEnabled()) {
        return;
    }
    // Use console.log (visible by default). Chrome DevTools hides
    // console.debug unless the "Verbose" log level is explicitly enabled,
    // which makes the trace easy to miss when investigating bugs.
    // eslint-disable-next-line no-console
    console.log(`[${namespace}]`, ...args.map(formatVecsInline));
}

/**
 * Log a one-shot startup confirmation when traces are enabled, so a
 * developer who set the localStorage flag can verify the wiring is
 * working before reproducing a bug.
 */
if (typeof window !== 'undefined' && isEnabled()) {
    // eslint-disable-next-line no-console
    console.log(
        "%c[domainTrace] enabled%c — disable with localStorage.setItem('domainTrace', '0') + reload",
        'color: #0a0; font-weight: bold',
        'color: inherit; font-weight: normal'
    );
}

export const domainTrace = {
    isEnabled,
    session: (...args: unknown[]) => log('session', ...args),
    tracker: (...args: unknown[]) => log('tracker', ...args),
    service: (...args: unknown[]) => log('service', ...args),
    engine: (...args: unknown[]) => log('engine-adapter', ...args),
    bridge: (...args: unknown[]) => log('recoil-bridge', ...args),
    view: (...args: unknown[]) => log('view-hook', ...args),
    dicom: (...args: unknown[]) => log('dicom', ...args),
    patient: (...args: unknown[]) => log('patient', ...args),
    hydration: (...args: unknown[]) => log('hydration', ...args),
};
